import { useEffect } from 'react'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '../../lib/api'
import { ME_KEY } from '../account/hooks'
import { banUser, deleteMessage, fetchAfter, fetchAuthor, fetchBefore, fetchLatest, reportMessage, sendMessage, subscribeToMessages } from './api'
import type { Message } from './api'
import { addMessages, flatten, hasMessage, newestId, removeMessage } from './cache'
import type { ChatData } from './cache'

/** Query key of the chat. */
export const MESSAGES_KEY = ['messages'] as const

/**
 * The loaded messages, oldest first. Older pages come with `fetchNextPage`;
 * new messages come from {@link useLiveMessages}, so the query never refetches
 * on its own.
 * @returns The infinite query, its data flattened.
 */
export function useMessages() {
  return useInfiniteQuery({
    queryKey: MESSAGES_KEY,
    queryFn: ({ pageParam }) => (pageParam === null ? fetchLatest() : fetchBefore(pageParam)),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => (last.has_more ? last.messages[0]?.id : undefined),
    select: flatten,
    staleTime: Infinity,
    refetchOnReconnect: false,
  })
}

/**
 * Keeps the chat up to date: Realtime inserts and deletes go straight into the
 * query cache, and whatever was missed while offline or in a background tab is
 * fetched from the API when the connection or the tab comes back.
 */
export function useLiveMessages(): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    const read = () => queryClient.getQueryData<ChatData>(MESSAGES_KEY)
    const add = (messages: Message[]) =>
      queryClient.setQueryData<ChatData>(MESSAGES_KEY, (data) => data && addMessages(data, messages))

    let catchingUp = false
    async function catchUp() {
      let after = newestId(read())
      if (after === undefined || catchingUp) return
      catchingUp = true
      try {
        for (;;) {
          const page = await fetchAfter(after)
          add(page.messages)
          const last = page.messages.at(-1)
          if (!page.has_more || !last) break
          after = last.id
        }
      } catch {
        // Offline again: the next reconnection retries.
      } finally {
        catchingUp = false
      }
    }

    let disconnected = false
    const unsubscribe = subscribeToMessages({
      onInsert: (row) => {
        if (hasMessage(read(), row.id)) return
        const author = row.user_id
          ? queryClient.fetchQuery({
              queryKey: ['author', row.user_id],
              queryFn: () => fetchAuthor(row.user_id as string),
              staleTime: 5 * 60_000,
            })
          : Promise.resolve(null)
        void author
          .catch(() => null)
          .then((author) => add([{ id: row.id, content: row.content, created_at: row.created_at, author }]))
      },
      onDelete: (id) => queryClient.setQueryData<ChatData>(MESSAGES_KEY, (data) => data && removeMessage(data, id)),
      onStatus: (status) => {
        if (status !== 'SUBSCRIBED') {
          disconnected = true
        } else if (disconnected) {
          disconnected = false
          void catchUp()
        }
      },
    })

    const onVisible = () => {
      if (document.visibilityState === 'visible') void catchUp()
    }
    window.addEventListener('online', catchUp)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      unsubscribe()
      window.removeEventListener('online', catchUp)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [queryClient])
}

/**
 * Sends a message and shows it at once. A `banned` answer refreshes the
 * profile, so the reason of the ban shows in place of the message field.
 * @returns The mutation.
 */
export function useSendMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: sendMessage,
    onSuccess: (message) =>
      queryClient.setQueryData<ChatData>(MESSAGES_KEY, (data) => data && addMessages(data, [message])),
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'banned') void queryClient.invalidateQueries({ queryKey: ME_KEY })
    },
  })
}

/**
 * Reports a message.
 * @returns The mutation, called with the message id and the reason.
 */
export function useReport() {
  return useMutation({
    mutationFn: ({ messageId, reason }: { messageId: number; reason: string }) => reportMessage(messageId, reason),
  })
}

/**
 * Deletes a message (moderators).
 * @returns The mutation, called with the message id.
 */
export function useDeleteMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteMessage,
    onSuccess: (_, id) => {
      queryClient.setQueryData<ChatData>(MESSAGES_KEY, (data) => data && removeMessage(data, id))
      void queryClient.invalidateQueries({ queryKey: ['moderation'] })
    },
  })
}

/**
 * Bans a user (moderators).
 * @returns The mutation, called with the user id, the reason and the end of the ban.
 */
export function useBan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, reason, until }: { userId: string; reason: string; until: Date | null }) =>
      banUser(userId, reason, until),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['moderation'] }),
  })
}
